import { collection, doc, addDoc, updateDoc, deleteDoc, getDoc, query, where, orderBy, onSnapshot, serverTimestamp } from 'firebase/firestore';
import { db } from '@/lib/firebase';
import type { WeatherForecast } from '@/services/weather';

export type ChecklistItemGroup = 'planning' | 'packing';

export type ChecklistItemSource =
  | 'booking'
  | 'document'
  | 'weather'
  | 'city_context'
  | 'travel_requirements'
  | 'manual'
  | 'auto';

export type ChecklistItem = {
  id: string;
  title: string;
  completed: boolean;
  dueDate?: Date;
  category?: 'visa' | 'insurance' | 'booking' | 'documents' | 'preparation' | 'health' | 'transportation' | 'activities' | 'clothing' | 'essentials' | 'other';
  notes?: string;
  tripId: string;
  createdBy: string;
  createdAt: Date;
  updatedAt: Date;
  group?: ChecklistItemGroup;
  autoGenerated?: boolean;
  source?: ChecklistItemSource;
  sourceId?: string; // ID of the source (booking ID, document ID, etc.)
};

export type Checklist = {
  id: string;
  tripId: string;
  name: string;
  items: ChecklistItem[];
  createdBy: string;
  createdAt: Date;
  updatedAt: Date;
};

const COLLECTION_NAME = 'checklists';

export function subscribeToTripChecklist(
  tripId: string,
  callback: (checklist: Checklist | null) => void
): () => void {
  const q = query(
    collection(db, COLLECTION_NAME),
    where('tripId', '==', tripId),
    orderBy('createdAt', 'desc')
  );

  return onSnapshot(
    q,
    (snapshot) => {
      if (snapshot.empty) {
        callback(null);
        return;
      }

      const doc = snapshot.docs[0];
      const data = doc.data();
      callback({
        id: doc.id,
        ...data,
        items: (data.items || []).map((item: any) => ({
          ...item,
          dueDate: item.dueDate?.toDate(),
          createdAt: item.createdAt?.toDate() || new Date(),
          updatedAt: item.updatedAt?.toDate() || new Date(),
        })),
        createdAt: data.createdAt?.toDate() || new Date(),
        updatedAt: data.updatedAt?.toDate() || new Date(),
      } as Checklist);
    },
    (error) => {
      console.error('[Checklists] Error subscribing:', error);
      callback(null);
    }
  );
}

export async function createChecklist(
  tripId: string,
  userId: string,
  name: string = 'Pre-Trip Checklist'
): Promise<string> {
  const checklistRef = await addDoc(collection(db, COLLECTION_NAME), {
    tripId,
    name,
    items: [],
    createdBy: userId,
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  });

  return checklistRef.id;
}

export async function addChecklistItem(
  checklistId: string,
  item: Omit<ChecklistItem, 'id' | 'createdAt' | 'updatedAt' | 'tripId' | 'createdBy'>
): Promise<void> {
  const checklistRef = doc(db, COLLECTION_NAME, checklistId);
  const checklistDoc = await getDoc(checklistRef);
  
  if (!checklistDoc.exists()) {
    throw new Error('Checklist not found');
  }

  const data = checklistDoc.data();
  const items = data?.items || [];
  
  const newItem: ChecklistItem = {
    id: `item-${Date.now()}`,
    ...item,
    tripId: data.tripId,
    createdBy: data.createdBy,
    createdAt: new Date(),
    updatedAt: new Date(),
  };

  await updateDoc(checklistRef, {
    items: [...items, newItem],
    updatedAt: serverTimestamp(),
  });
}

export async function updateChecklistItem(
  checklistId: string,
  itemId: string,
  updates: Partial<Omit<ChecklistItem, 'id' | 'createdAt' | 'updatedAt' | 'tripId' | 'createdBy'>>
): Promise<void> {
  // Strip undefined to avoid Firestore invalid data errors
  const cleanedUpdates = Object.entries(updates).reduce<Record<string, unknown>>((acc, [key, value]) => {
    if (value !== undefined) acc[key] = value;
    return acc;
  }, {});

  const checklistRef = doc(db, COLLECTION_NAME, checklistId);
  const checklistDoc = await getDoc(checklistRef);
  
  if (!checklistDoc.exists()) {
    throw new Error('Checklist not found');
  }

  const data = checklistDoc.data();
  const items = (data?.items || []) as ChecklistItem[];
  
  const updatedItems = items.map(item =>
    item.id === itemId
      ? { ...item, ...cleanedUpdates, updatedAt: new Date() }
      : item
  );

  await updateDoc(checklistRef, {
    items: updatedItems,
    updatedAt: serverTimestamp(),
  });
}

export async function deleteChecklistItem(
  checklistId: string,
  itemId: string
): Promise<void> {
  const checklistRef = doc(db, COLLECTION_NAME, checklistId);
  const checklistDoc = await getDoc(checklistRef);
  
  if (!checklistDoc.exists()) {
    throw new Error('Checklist not found');
  }

  const data = checklistDoc.data();
  const items = (data?.items || []) as ChecklistItem[];
  
  const filteredItems = items.filter(item => item.id !== itemId);

  await updateDoc(checklistRef, {
    items: filteredItems,
    updatedAt: serverTimestamp(),
  });
}

/**
 * Generate planning checklist items based on trip data, bookings, documents, and travel requirements
 */
export async function generatePlanningChecklist(
  tripId: string,
  userId: string,
  trip: { startDate: Date; endDate: Date; destination?: string; destinations?: Array<{ city: string; country?: string }> },
  bookings: Array<{ type: string; id: string }>,
  documents: Array<{ type: string; id: string; metadata?: any; expiresOn?: Date | null }>,
  travelRequirements?: {
    visaRequired?: boolean;
    visaStatus?: string;
    healthRequirements?: string[];
    notes?: string;
  },
  passportCountry?: string
): Promise<Omit<ChecklistItem, 'id' | 'createdAt' | 'updatedAt' | 'tripId' | 'createdBy'>[]> {
  const items: Omit<ChecklistItem, 'id' | 'createdAt' | 'updatedAt' | 'tripId' | 'createdBy'>[] = [];
  const tripStart = trip.startDate instanceof Date ? trip.startDate : new Date(trip.startDate);
  const tripEnd = trip.endDate instanceof Date ? trip.endDate : new Date(trip.endDate);

  // Visa requirements
  if (travelRequirements?.visaRequired) {
    items.push({
      title: `Apply for ${travelRequirements.visaStatus === 'e_visa' ? 'e-visa' : travelRequirements.visaStatus === 'visa_on_arrival' ? 'visa on arrival' : 'visa'}`,
      completed: false,
      category: 'visa',
      group: 'planning',
      autoGenerated: true,
      source: 'travel_requirements',
      dueDate: new Date(tripStart.getTime() - 30 * 24 * 60 * 60 * 1000), // 30 days before
      notes: travelRequirements.notes || 'Visa required for this destination',
    });
  } else if (travelRequirements && !travelRequirements.visaRequired) {
    items.push({
      title: 'Confirm visa-free entry eligibility',
      completed: false,
      category: 'visa',
      group: 'planning',
      autoGenerated: true,
      source: 'travel_requirements',
      notes: `No visa required${passportCountry ? ` for ${passportCountry} citizens` : ''}`,
    });
  } else {
    // Always add visa check item even if travel requirements aren't available
    items.push({
      title: 'Check visa requirements',
      completed: false,
      category: 'visa',
      group: 'planning',
      autoGenerated: true,
      source: 'auto',
      dueDate: new Date(tripStart.getTime() - 30 * 24 * 60 * 60 * 1000), // 30 days before
      notes: 'Verify visa requirements for your destination',
    });
  }

  // Health requirements
  if (travelRequirements?.healthRequirements && travelRequirements.healthRequirements.length > 0) {
    travelRequirements.healthRequirements.forEach((req, index) => {
      items.push({
        title: req,
        completed: false,
        category: 'health',
        group: 'planning',
        autoGenerated: true,
        source: 'travel_requirements',
        dueDate: new Date(tripStart.getTime() - 14 * 24 * 60 * 60 * 1000), // 14 days before
      });
    });
  }

  // Passport/ID ready
  const passport = documents.find(doc => doc.type === 'passport');
  if (passport) {
    const passportExp = passport.expiresOn;
    const sixMonthsAfterTrip = new Date(tripEnd);
    sixMonthsAfterTrip.setMonth(sixMonthsAfterTrip.getMonth() + 6);
    
    items.push({
      title: 'Passport/ID ready and valid',
      completed: passportExp ? passportExp > sixMonthsAfterTrip : false,
      category: 'documents',
      group: 'planning',
      autoGenerated: true,
      source: 'document',
      sourceId: passport.id,
      notes: passportExp && passportExp <= sixMonthsAfterTrip 
        ? `⚠️ Passport expires ${passportExp.toLocaleDateString()}. Most countries require 6+ months validity.`
        : 'Passport is valid for travel',
    });
  } else {
    items.push({
      title: 'Passport/ID ready and valid',
      completed: false,
      category: 'documents',
      group: 'planning',
      autoGenerated: true,
      source: 'auto',
      notes: 'Ensure passport is valid for at least 6 months after trip end date',
    });
  }

  // Travel insurance
  const insurance = documents.find(doc => doc.type === 'insurance');
  items.push({
    title: 'Travel insurance (optional)',
    completed: !!insurance,
    category: 'insurance',
    group: 'planning',
    autoGenerated: true,
    source: insurance ? 'document' : 'auto',
    sourceId: insurance?.id,
    notes: insurance ? 'Travel insurance is set up' : 'Consider purchasing travel insurance',
  });

  // WiFi/cellphone plan
  items.push({
    title: 'WiFi/cellphone plan',
    completed: false,
    category: 'preparation',
    group: 'planning',
    autoGenerated: true,
    source: 'auto',
    notes: 'Set up international roaming or local SIM card',
  });

  // Medications
  items.push({
    title: 'Medications',
    completed: false,
    category: 'preparation',
    group: 'planning',
    autoGenerated: true,
    source: 'auto',
    notes: 'Pack necessary medications and prescriptions',
  });

  // Flight booked
  const flight = bookings.find(b => b.type === 'flight');
  items.push({
    title: 'Flight booked',
    completed: !!flight,
    category: 'booking',
    group: 'planning',
    autoGenerated: true,
    source: flight ? 'booking' : 'auto',
    sourceId: flight?.id,
  });

  // Lodging booked
  const hotel = bookings.find(b => b.type === 'hotel');
  items.push({
    title: 'Lodging booked',
    completed: !!hotel,
    category: 'booking',
    group: 'planning',
    autoGenerated: true,
    source: hotel ? 'booking' : 'auto',
    sourceId: hotel?.id,
  });

  // Transportation planned
  const transportation = bookings.find(b => ['car', 'transportation', 'ferry'].includes(b.type));
  items.push({
    title: 'Transportation mode planned',
    completed: !!transportation,
    category: 'transportation',
    group: 'planning',
    autoGenerated: true,
    source: transportation ? 'booking' : 'auto',
    sourceId: transportation?.id,
    notes: transportation ? 'Transportation is booked' : 'Plan car rental, public transport card, etc.',
  });

  // Activities reserved
  const activities = bookings.filter(b => ['activity', 'tour', 'attraction'].includes(b.type));
  items.push({
    title: 'Activities and tours reserved',
    completed: activities.length > 0,
    category: 'activities',
    group: 'planning',
    autoGenerated: true,
    source: activities.length > 0 ? 'booking' : 'auto',
    notes:
      activities.length > 0
        ? `${activities.length} ${activities.length === 1 ? 'activity' : 'activities'} booked`
        : 'Reserve activities and tours',
  });

  return items;
}

/**
 * Generate packing list items based on weather and essentials
 */
export async function generatePackingList(
  tripId: string,
  userId: string,
  trip: { startDate: Date; endDate: Date; destination?: string; destinations?: Array<{ city: string; country?: string }> },
  weatherForecast?: WeatherForecast
): Promise<Omit<ChecklistItem, 'id' | 'createdAt' | 'updatedAt' | 'tripId' | 'createdBy'>[]> {
  const items: Omit<ChecklistItem, 'id' | 'createdAt' | 'updatedAt' | 'tripId' | 'createdBy'>[] = [];
  
  // Clothing based on weather
  if (weatherForecast?.forecast && weatherForecast.forecast.length > 0) {
    const temps = weatherForecast.forecast.map(f => ({ 
      min: f.temperature.min, 
      max: f.temperature.max 
    }));
    const avgMin = temps.reduce((sum, t) => sum + t.min, 0) / temps.length;
    const avgMax = temps.reduce((sum, t) => sum + t.max, 0) / temps.length;
    const hasPrecipitation = weatherForecast.forecast.some(f => 
      f.precipitation && (f.precipitation.probability > 0 || (f.precipitation.amount || 0) > 0)
    );

    // Temperature-based clothing
    if (avgMax > 25) {
      items.push({
        title: 'Light clothing (shorts, t-shirts, summer dresses)',
        completed: false,
        category: 'clothing',
        group: 'packing',
        autoGenerated: true,
        source: 'weather',
        notes: `Average high: ${Math.round(avgMax)}°C`,
      });
    } else if (avgMax > 15) {
      items.push({
        title: 'Moderate clothing (light layers, long sleeves)',
        completed: false,
        category: 'clothing',
        group: 'packing',
        autoGenerated: true,
        source: 'weather',
        notes: `Average temp: ${Math.round(avgMin)}-${Math.round(avgMax)}°C`,
      });
    } else {
      items.push({
        title: 'Warm clothing (jackets, sweaters, layers)',
        completed: false,
        category: 'clothing',
        group: 'packing',
        autoGenerated: true,
        source: 'weather',
        notes: `Average temp: ${Math.round(avgMin)}-${Math.round(avgMax)}°C`,
      });
    }

    if (hasPrecipitation) {
      items.push({
        title: 'Rain gear (umbrella, rain jacket)',
        completed: false,
        category: 'clothing',
        group: 'packing',
        autoGenerated: true,
        source: 'weather',
        notes: 'Rain expected during trip',
      });
    }
  } else {
    // Default clothing if no weather data
    items.push({
      title: 'Appropriate clothing for destination',
      completed: false,
      category: 'clothing',
      group: 'packing',
      autoGenerated: true,
      source: 'auto',
      notes: 'Pack clothing suitable for destination climate and culture',
    });
  }

  // Essentials
  const essentials = [
    { title: 'Toothbrush', category: 'essentials' as const },
    { title: 'Toothpaste', category: 'essentials' as const },
    { title: 'Medications', category: 'essentials' as const },
    { title: 'Passport & IDs', category: 'essentials' as const },
    { title: 'PJs', category: 'essentials' as const },
    { title: 'Chargers/adapters', category: 'essentials' as const },
    { title: 'Phone charger', category: 'essentials' as const },
    { title: 'Power bank', category: 'essentials' as const },
  ];

  essentials.forEach(essential => {
    items.push({
      title: essential.title,
      completed: false,
      category: essential.category,
      group: 'packing',
      autoGenerated: true,
      source: 'auto',
    });
  });

  return items;
}

